import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit,
  Person,
  Email,
  Phone,
  Badge,
  PhotoCamera,
  ArrowBack,
  Save,
  Cancel,
  Thermostat,
  Air,
  Add,
  Upload,
  CameraAlt,
  Spa,
  Image
} from '@mui/icons-material';
import reservationService from '../services/reservationService';
import s3Service from '../services/s3Service';
import { useLanguage } from '../App';

// Guest Information View translations
const guestViewTranslations = {
  en: {
    guest_information_title: 'Guest Information',
    reservation_details: 'Reservation Details',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Guests',
    status: 'Status',
    guest: 'Guest',
    complete: 'Complete',
    incomplete: 'Incomplete',
    no_email: 'No email provided',
    no_phone: 'No phone provided',
    not_provided: 'Not provided',
    photo: 'Photo',
    uploaded: 'Uploaded',
    missing: 'Missing',
    guest_preferences: 'Guest Preferences',
    aromatherapy: 'Aromatherapy',
    air_conditioning: 'Air Conditioning',
    temperature: 'Temperature',
    yes: 'Yes',
    no: 'No',
    edit_guest_info: 'Edit Guest Information',
    edit_preferences: 'Edit Guest Preferences',
    save_changes: 'Save Changes',
    cancel: 'Cancel',
    loading: 'Loading guest information...',
    error_loading: 'Failed to load reservation data',
    photo_stored_s3: 'Photo stored in S3',
    photo_local_data: 'Photo (local data)',
    view_photo: 'View Photo'
  },
  pt: {
    guest_information_title: 'Informações do Hóspede',
    reservation_details: 'Detalhes da Reserva',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Hóspedes',
    status: 'Status',
    guest: 'Hóspede',
    complete: 'Completo',
    incomplete: 'Incompleto',
    no_email: 'Nenhum email fornecido',
    no_phone: 'Nenhum telefone fornecido',
    not_provided: 'Não fornecido',
    photo: 'Foto',
    uploaded: 'Enviada',
    missing: 'Ausente',
    guest_preferences: 'Preferências do Hóspede',
    aromatherapy: 'Aromaterapia',
    air_conditioning: 'Ar Condicionado',
    temperature: 'Temperatura',
    yes: 'Sim',
    no: 'Não',
    edit_guest_info: 'Editar Informações do Hóspede',
    edit_preferences: 'Editar Preferências',
    save_changes: 'Salvar Alterações',
    cancel: 'Cancelar',
    loading: 'Carregando informações do hóspede...',
    error_loading: 'Falha ao carregar dados da reserva',
    photo_stored_s3: 'Foto armazenada no S3',
    photo_local_data: 'Foto (dados locais)',
    view_photo: 'Ver Foto'
  },
  es: {
    guest_information_title: 'Información del Huésped',
    reservation_details: 'Detalles de la Reserva',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Huéspedes',
    status: 'Estado',
    guest: 'Huésped',
    complete: 'Completo',
    incomplete: 'Incompleto',
    no_email: 'No se proporcionó email',
    no_phone: 'No se proporcionó teléfono',
    not_provided: 'No proporcionado',
    photo: 'Foto',
    uploaded: 'Subida',
    missing: 'Faltante',
    guest_preferences: 'Preferencias del Huésped',
    aromatherapy: 'Aromaterapia',
    air_conditioning: 'Aire Acondicionado',
    temperature: 'Temperatura',
    yes: 'Sí',
    no: 'No',
    edit_guest_info: 'Editar Información del Huésped',
    edit_preferences: 'Editar Preferencias',
    save_changes: 'Guardar Cambios',
    cancel: 'Cancelar',
    loading: 'Cargando información del huésped...',
    error_loading: 'Error al cargar datos de la reserva',
    photo_stored_s3: 'Foto almacenada en S3',
    photo_local_data: 'Foto (datos locales)',
    view_photo: 'Ver Foto'
  }
};

const GuestInformationView = () => {
  const { id: reservationId } = useParams(); // Fix: extract 'id' parameter and rename to reservationId
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoViewDialog, setPhotoViewDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [addingGuest, setAddingGuest] = useState(false);
  const [newGuest, setNewGuest] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    documentType: 'cpf',
    documentNumber: '',
    photo: null,
    completed: false
  });

  const t = (key) => {
    return guestViewTranslations[language]?.[key] || guestViewTranslations.en[key] || key;
  };

  useEffect(() => {
    loadReservationData();
  }, [reservationId]);

  const loadReservationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('=== DEBUG: Starting loadReservationData ===');
      console.log('reservationId:', reservationId);
      
      if (!reservationId) {
        console.error('No reservationId provided');
        setError(t('error_loading'));
        return;
      }
      
      console.log('Making API call to get reservation:', reservationId);
      const data = await reservationService.getReservation(reservationId);
      console.log('API response received:', data);
      
      if (!data) {
        console.error('No data returned from API');
        setError(t('error_loading'));
        return;
      }
      
      console.log('Setting reservation data:', data);
      setReservation(data);
      console.log('=== DEBUG: loadReservationData completed successfully ===');
    } catch (err) {
      console.error('=== DEBUG: Error in loadReservationData ===');
      console.error('Error details:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setError(t('error_loading') + ': ' + (err.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditGuest = (guest) => {
    setEditingGuest({ ...guest });
    setEditMode(true);
  };

  const handleSaveGuest = async () => {
    try {
      setSaving(true);
      
      // Update the guest in the reservation data
      const updatedGuestInfo = reservation.partialGuestInformation || reservation.guestInformation || [];
      const guestIndex = updatedGuestInfo.findIndex(g => g.id === editingGuest.id);
      
      if (guestIndex >= 0) {
        updatedGuestInfo[guestIndex] = editingGuest;
      }
      
      // Save to backend
      const updateData = {
        partialGuestInformation: updatedGuestInfo,
        preferences: reservation.preferences || {},
        partiallyCompleted: true,
        lastUpdated: new Date().toISOString()
      };
      
      await reservationService.savePartialGuestInformation(reservationId, updateData);
      
      // Update local state
      setReservation(prev => ({
        ...prev,
        partialGuestInformation: updatedGuestInfo
      }));
      
      setEditMode(false);
      setEditingGuest(null);
      
    } catch (err) {
      console.error('Error saving guest:', err);
      setError('Failed to save guest information');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPreferences = () => {
    setEditingPreferences(true);
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        partialGuestInformation: reservation.partialGuestInformation || reservation.guestInformation || [],
        preferences: reservation.preferences,
        partiallyCompleted: true,
        lastUpdated: new Date().toISOString()
      };
      
      await reservationService.savePartialGuestInformation(reservationId, updateData);
      setEditingPreferences(false);
      
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleViewPhoto = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setPhotoViewDialog(true);
  };

  const getGuestInformation = () => {
    if (!reservation) return [];
    
    // Try different possible locations for guest information
    const guestInfo = reservation.guestInformation || 
                     reservation.partialGuestInformation || 
                     [];
    
    console.log('Guest information found:', guestInfo);
    return Array.isArray(guestInfo) ? guestInfo : [];
  };

  const handlePhotoUpload = async (guestIndex, file) => {
    try {
      setSaving(true);
      
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Photo = e.target.result;
        
        // Update guest photo
        const guestInfo = getGuestInformation();
        const updatedGuests = [...guestInfo];
        if (updatedGuests[guestIndex]) {
          updatedGuests[guestIndex] = {
            ...updatedGuests[guestIndex],
            photo: base64Photo,
            photoSource: 'captured'
          };
        }
        
        // Save to backend
        const updateData = {
          guestInformation: updatedGuests,
          lastUpdated: new Date().toISOString()
        };
        
        await reservationService.savePartialGuestInformation(reservationId, updateData);
        
        // Reload reservation data
        await loadReservationData();
      };
      
      reader.readAsDataURL(file);
      
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGuest = async () => {
    try {
      setSaving(true);
      
      const guestInfo = getGuestInformation();
      const newGuestWithId = {
        ...newGuest,
        id: guestInfo.length + 1,
        completed: true
      };
      
      const updatedGuests = [...guestInfo, newGuestWithId];
      
      const updateData = {
        guestInformation: updatedGuests,
        lastUpdated: new Date().toISOString()
      };
      
      await reservationService.savePartialGuestInformation(reservationId, updateData);
      
      // Reset form and reload
      setNewGuest({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        documentType: 'cpf',
        documentNumber: '',
        photo: null,
        completed: false
      });
      setAddingGuest(false);
      await loadReservationData();
      
    } catch (err) {
      console.error('Error adding guest:', err);
      setError('Failed to add guest');
    } finally {
      setSaving(false);
    }
  };

  const handleNewGuestPhotoUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewGuest(prev => ({
        ...prev,
        photo: e.target.result,
        photoSource: 'captured'
      }));
    };
    reader.readAsDataURL(file);
  };

  const getPreferences = () => {
    if (!reservation) return {};
    
    const preferences = reservation.preferences || {};
    console.log('Preferences found:', preferences);
    return preferences;
  };

  const renderPhotoStatus = (guest, guestIndex) => {
    const photoStatus = s3Service.getPhotoStatus(guest.photo);
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhotoCamera fontSize="small" color="action" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Photo: {photoStatus.uploaded ? 
              <Chip label={photoStatus.type === 'S3' ? t('photo_stored_s3') : t('photo_local_data')} color="success" size="small" /> : 
              <Chip label="Missing" color="error" size="small" />
            }
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            {guest.photo && (
              <Button 
                size="small" 
                startIcon={<Image />}
                onClick={() => handleViewPhoto(s3Service.getPhotoDisplayUrl(guest.photo))}
              >
                {t('view_photo')}
              </Button>
            )}
            <Button
              size="small"
              startIcon={<Upload />}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handlePhotoUpload(guestIndex, file);
                  }
                };
                input.click();
              }}
              disabled={saving}
            >
              {guest.photo ? 'Replace Photo' : 'Upload Photo'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" align="center">{t('loading')}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const guestInfo = getGuestInformation();
  const preferences = getPreferences();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/reservations')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {t('guest_information_title')} - {reservation?.listingNickname || reservationId}
        </Typography>
      </Box>

      {/* Reservation Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('reservation_details')}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">{t('check_in')}</Typography>
              <Typography variant="body1">{reservation?.checkInDate}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">{t('check_out')}</Typography>
              <Typography variant="body1">{reservation?.checkOutDate}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">{t('guests')}</Typography>
              <Typography variant="body1">{reservation?.numberOfGuests || reservation?.numberOfAdults}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="body2" color="text.secondary">{t('status')}</Typography>
              <Chip 
                label={reservation?.status || 'Unknown'} 
                color={reservation?.status === 'Guest Information Provided' ? 'success' : 'primary'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Guest Information Cards */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Guest Information ({guestInfo.length})
      </Typography>
      
      <Grid container spacing={3}>
        {guestInfo.map((guest, index) => (
          <Grid item xs={12} md={6} key={guest.id || index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        Guest {index + 1}
                      </Typography>
                      <Chip 
                        label={guest.completed ? 'Complete' : 'Incomplete'} 
                        color={guest.completed ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <IconButton onClick={() => handleEditGuest(guest)} color="primary">
                    <Edit />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body1">
                        {guest.firstName} {guest.lastName}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {guest.email || 'No email provided'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {guest.phone || 'No phone provided'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Badge fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {guest.documentType?.toUpperCase()}: {guest.documentNumber || 'Not provided'}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    {renderPhotoStatus(guest, index)}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {/* Add Guest Button */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: '2px dashed #ccc',
            cursor: 'pointer'
          }}
          onClick={() => setAddingGuest(true)}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Add sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Add New Guest
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click to add guest information
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preferences Section */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Guest Preferences</Typography>
            <IconButton onClick={handleEditPreferences} color="primary">
              <Edit />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Spa color={preferences.aromatherapy ? 'success' : 'disabled'} />
                <Box>
                  <Typography variant="body1">Aromatherapy</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {preferences.aromatherapy ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Air color={preferences.airConditioning ? 'primary' : 'disabled'} />
                <Box>
                  <Typography variant="body1">Air Conditioning</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {preferences.airConditioning ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {preferences.airConditioning && (
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Thermostat color="primary" />
                  <Box>
                    <Typography variant="body1">Temperature</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {preferences.temperature}°C ({Math.round((preferences.temperature * 9/5) + 32)}°F)
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Photo View Dialog */}
      <Dialog 
        open={photoViewDialog} 
        onClose={() => setPhotoViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Guest Photo</DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <Box sx={{ textAlign: 'center' }}>
              <img 
                src={selectedPhoto} 
                alt="Guest Photo" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '500px', 
                  objectFit: 'contain' 
                }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Guest Dialog */}
      <Dialog open={editMode} onClose={() => setEditMode(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Guest Information</DialogTitle>
        <DialogContent>
          {editingGuest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editingGuest.firstName || ''}
                  onChange={(e) => setEditingGuest(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editingGuest.lastName || ''}
                  onChange={(e) => setEditingGuest(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editingGuest.email || ''}
                  onChange={(e) => setEditingGuest(prev => ({ ...prev, email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editingGuest.phone || ''}
                  onChange={(e) => setEditingGuest(prev => ({ ...prev, phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={editingGuest.documentType || ''}
                    onChange={(e) => setEditingGuest(prev => ({ ...prev, documentType: e.target.value }))}
                    label="Document Type"
                  >
                    <MenuItem value="cpf">CPF (Brazilian)</MenuItem>
                    <MenuItem value="passport">Passport (International)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Document Number"
                  value={editingGuest.documentNumber || ''}
                  onChange={(e) => setEditingGuest(prev => ({ ...prev, documentNumber: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMode(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveGuest} 
            variant="contained" 
            startIcon={<Save />}
            disabled={saving}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Preferences Dialog */}
      <Dialog open={editingPreferences} onClose={() => setEditingPreferences(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Guest Preferences</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Aromatherapy</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={reservation?.preferences?.aromatherapy ? 'contained' : 'outlined'}
                  color="success"
                  onClick={() => setReservation(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, aromatherapy: true }
                  }))}
                >
                  Yes
                </Button>
                <Button
                  variant={!reservation?.preferences?.aromatherapy ? 'contained' : 'outlined'}
                  onClick={() => setReservation(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, aromatherapy: false }
                  }))}
                >
                  No
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Air Conditioning</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant={reservation?.preferences?.airConditioning ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setReservation(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, airConditioning: true }
                  }))}
                >
                  Yes
                </Button>
                <Button
                  variant={!reservation?.preferences?.airConditioning ? 'contained' : 'outlined'}
                  onClick={() => setReservation(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, airConditioning: false }
                  }))}
                >
                  No
                </Button>
              </Box>
              
              {reservation?.preferences?.airConditioning && (
                <TextField
                  type="number"
                  label="Temperature (°C)"
                  value={reservation?.preferences?.temperature || 22}
                  onChange={(e) => setReservation(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, temperature: parseInt(e.target.value) }
                  }))}
                  inputProps={{ min: 16, max: 30 }}
                  helperText="16-30°C (61-86°F)"
                  sx={{ maxWidth: 200 }}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingPreferences(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePreferences} 
            variant="contained" 
            startIcon={<Save />}
            disabled={saving}
          >
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Guest Dialog */}
      <Dialog open={addingGuest} onClose={() => setAddingGuest(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Guest</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newGuest.firstName}
                onChange={(e) => setNewGuest(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newGuest.lastName}
                onChange={(e) => setNewGuest(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newGuest.email}
                onChange={(e) => setNewGuest(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={newGuest.phone}
                onChange={(e) => setNewGuest(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={newGuest.documentType}
                  onChange={(e) => setNewGuest(prev => ({ ...prev, documentType: e.target.value }))}
                  label="Document Type"
                >
                  <MenuItem value="cpf">CPF (Brazil)</MenuItem>
                  <MenuItem value="passport">Passport</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Document Number"
                value={newGuest.documentNumber}
                onChange={(e) => setNewGuest(prev => ({ ...prev, documentNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CameraAlt />}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleNewGuestPhotoUpload(file);
                      }
                    };
                    input.click();
                  }}
                >
                  {newGuest.photo ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {newGuest.photo && (
                  <Chip label="Photo uploaded" color="success" size="small" />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddingGuest(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddGuest} 
            variant="contained" 
            startIcon={<Save />}
            disabled={saving || !newGuest.firstName || !newGuest.lastName}
          >
            Add Guest
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GuestInformationView;
